/**
 * The arrangements a front page can take.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add one, add a row to the table below and a `.cover--<id>` block to
 * `CoverBlock.vue`.** The type, the list of ids and the picker all follow.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Six rather than one because a front page is the part of a report people
 * actually have an opinion about, and because the same document goes out to
 * different audiences: a statutory filing wants the framed, sober one; a
 * sustainability statement published on a website wants the photograph.
 *
 * Every variant is built from the same handful of parts, in the same order in
 * the markup. What differs is where they sit and how much room each is given,
 * which is a stylesheet's job. That keeps a variant a layout rather than a
 * separate component with its own bugs.
 *
 * `picture` and `logo` say what each arrangement does with an image, so the
 * inspector can offer the right upload and say what it will be used for
 * rather than offering both and leaving somebody to find out.
 */

export const COVER_VARIANTS = {
  /** The block in the upper third, everything ranged left. A report. */
  classic: { picture: false, logo: true },
  /** Centred, generous, with the logo above. An annual report. */
  centred: { picture: false, logo: true },
  /** A band of the house colour across the head, the title reversed out. */
  band: { picture: false, logo: true },
  /** A photograph filling the upper half, the block beneath it. */
  portrait: { picture: true, logo: true },
  /** A thin rule around the whole page, the block centred inside. Statutory. */
  framed: { picture: false, logo: true },
  /** A colour column down the leading edge, the block beside it. */
  sidebar: { picture: true, logo: true },
  /**
   * A single photograph, bled to every edge of the sheet, with nothing drawn
   * over it: no logo, no title block, no note.
   *
   * For a cover finished elsewhere — a design tool, a page exported as one
   * image — where the document should carry the picture as-is rather than
   * lay its own text on top of somebody else's composition.
   */
  full: { picture: true, logo: false },
} as const

export type CoverVariant = keyof typeof COVER_VARIANTS

export const COVER_VARIANT_IDS = Object.keys(COVER_VARIANTS) as readonly CoverVariant[]

export const DEFAULT_COVER_VARIANT: CoverVariant = 'classic'

/** Whether this arrangement has somewhere to put a photograph. */
export function usesPicture(variant: CoverVariant): boolean {
  return COVER_VARIANTS[variant].picture
}

/** Whether this arrangement has somewhere to put a logo. */
export function usesLogo(variant: CoverVariant): boolean {
  return COVER_VARIANTS[variant].logo
}

/** A stored value, if it names an arrangement this version offers. */
export function readCoverVariant(value: unknown): CoverVariant {
  return typeof value === 'string' && value in COVER_VARIANTS
    ? (value as CoverVariant)
    : DEFAULT_COVER_VARIANT
}
